import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiMap, FiPlay, FiTarget } from 'react-icons/fi';

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      icon: FiSearch,
      title: "The Cognitive Map",
      description: "We conduct a comprehensive assessment to pinpoint the specific executive function deficits underlying your child's learning challenge. Is it a working memory bottleneck? A processing speed issue?"
    },
    {
      number: "02",
      icon: FiMap,
      title: "Your Custom Learning Blueprint",
      description: "We design a therapy plan that targets your child's unique cognitive profile, integrating their personal interests to maximize engagement and carryover."
    },
    {
      number: "03",
      icon: FiPlay,
      title: "Strategic Skill-Building Sessions",
      description: "Through weekly 1-on-1 sessions, we play games and tackle real-world academic challenges that feel like fun but are designed to systematically strengthen weak cognitive muscles."
    },
    {
      number: "04",
      icon: FiTarget,
      title: "Integration & Independence",
      description: "We help you and your child to use these new skills in school and at home, ensuring the strategies become automatic and your child becomes a resilient, independent learner."
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Your Child's Path to Becoming a Confident Learner in 4 Steps
          </h2>
        </motion.div>

        {/* Steps */}
        <div className="space-y-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              {/* Content */}
              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-accent-magenta to-accent-purple rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {step.number}
                  </div>
                  <div className={`w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center`}>
                    <SafeIcon icon={step.icon} className="h-6 w-6 text-accent-gold" />
                  </div>
                </div>
                <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900">
                  {step.title}
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Visual */}
              <div className={`${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="relative">
                  <div className={`w-full h-64 bg-gradient-to-br ${
                    index === 0 ? 'from-accent-magenta/20 to-accent-purple/20' :
                    index === 1 ? 'from-accent-gold/20 to-accent-yellow/20' :
                    index === 2 ? 'from-accent-purple/20 to-accent-lavender/20' :
                    'from-accent-lavender/20 to-accent-light/20'
                  } rounded-2xl flex items-center justify-center`}>
                    <SafeIcon 
                      icon={step.icon} 
                      className={`h-24 w-24 ${
                        index === 0 ? 'text-accent-magenta' :
                        index === 1 ? 'text-accent-gold' :
                        index === 2 ? 'text-accent-purple' :
                        'text-accent-lavender'
                      }`} 
                    />
                  </div>
                  
                  {/* Connecting line for non-last items */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-full left-1/2 transform -translate-x-1/2 w-px h-16 bg-gradient-to-b from-gray-300 to-transparent"></div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <button onClick={scrollToContact} className="btn-primary text-lg px-10 py-5">
            Start Step 1: Book Your Assessment
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;